import { useCallback, useEffect, useState } from "react";
import { ThrowError } from "@/api/callApi";
import type { branch } from "@/interface/branch.interface";
import type { ISalaryHead } from "@/interface/salaryHead.interface";
import { BranchService } from "@/services/Branch/BranchSevice";
import { SalaryHeadServices } from "@/services/SalaryHead/SalaryHeadServices";
import { showToastNotification } from "@/utility/helper";
import type {
  BranchSalaryHeadPayload,
  BranchSalaryHeadType,
  IBranchSalaryHead,
} from "../interface/branchSalaryHead.types";

interface SubmitBranchSalaryHeadParams {
  type: BranchSalaryHeadType;
  salaryHeadId: string;
  amount: string;
  isFixed: boolean;
  editingItem: IBranchSalaryHead | null;
}

export const useBranchSalaryHeadData = (routeBranchId?: string) => {
  const [salaryHeads, setSalaryHeads] = useState<ISalaryHead[]>([]);
  const [branchSalaryHeads, setBranchSalaryHeads] = useState<
    IBranchSalaryHead[]
  >([]);
  const [branches, setBranches] = useState<branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState(routeBranchId || "");
  const [isLoadingSalaryHeads, setIsLoadingSalaryHeads] = useState(false);
  const [isLoadingBranchSalaryHeads, setIsLoadingBranchSalaryHeads] =
    useState(false);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activeBranchId = routeBranchId || selectedBranchId;

  const fetchSalaryHeads = useCallback(async () => {
    setIsLoadingSalaryHeads(true);
    try {
      const [earningResponse, deductionResponse] = await Promise.all([
        SalaryHeadServices.getList("type=earning"),
        SalaryHeadServices.getList("type=deduction"),
      ]);

      setSalaryHeads([
        ...(earningResponse?.data?.salaryHead || []),
        ...(deductionResponse?.data?.salaryHead || []),
      ]);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoadingSalaryHeads(false);
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    if (routeBranchId) return;

    setIsLoadingBranches(true);
    try {
      const response = await BranchService.getBranch(
        "pageNumber=1&pageSize=100",
      );
      if (response?.status) {
        setBranches(response?.data?.branch || []);
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to fetch branches",
        );
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoadingBranches(false);
    }
  }, [routeBranchId]);

  const fetchBranchSalaryHeads = useCallback(async (branchId: string) => {
    if (!branchId) {
      setBranchSalaryHeads([]);
      return;
    }

    setIsLoadingBranchSalaryHeads(true);
    try {
      const response = await SalaryHeadServices.getBranchSalaryHeads(branchId);
      if (response?.status) {
        setBranchSalaryHeads(response?.data?.branchSalaryHead || []);
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to fetch branch salary heads",
        );
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoadingBranchSalaryHeads(false);
    }
  }, []);

  const submitBranchSalaryHead = useCallback(
    async ({
      type,
      salaryHeadId,
      amount,
      isFixed,
      editingItem,
    }: SubmitBranchSalaryHeadParams) => {
      const selectedSalaryHead = salaryHeads.find(
        (item) => item.id === salaryHeadId,
      );

      if (!activeBranchId) {
        showToastNotification("danger", "Please select branch");
        return false;
      }

      if (!salaryHeadId) {
        showToastNotification("danger", `Please select ${type} salary head`);
        return false;
      }

      if (selectedSalaryHead?.isBase) {
        showToastNotification("danger", "Base salary head cannot be selected");
        return false;
      }

      if (isFixed && !amount.trim()) {
        showToastNotification("danger", "Please enter amount");
        return false;
      }

      setIsSubmitting(true);
      try {
        const payload: BranchSalaryHeadPayload = {
          branchId: activeBranchId,
          salaryHeadId,
          isFixed,
          amount,
        };

        const response = editingItem
          ? await SalaryHeadServices.updateBranchSalaryHead(
              editingItem.id,
              payload,
            )
          : await SalaryHeadServices.createBranchSalaryHead(payload);

        if (response?.status) {
          showToastNotification(
            "success",
            response?.message ||
              `Branch salary head ${editingItem ? "updated" : "added"} successfully`,
          );
          fetchBranchSalaryHeads(activeBranchId);
          return true;
        }

        showToastNotification(
          "danger",
          response?.message || "Failed to save branch salary head",
        );
        return false;
      } catch (error) {
        ThrowError(error);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [activeBranchId, fetchBranchSalaryHeads, salaryHeads],
  );

  const deleteBranchSalaryHead = useCallback(
    async (branchSalaryHeadId: string) => {
      setIsSubmitting(true);
      try {
        const response =
          await SalaryHeadServices.deleteBranchSalaryHead(branchSalaryHeadId);
        if (response?.status) {
          showToastNotification(
            "success",
            response?.message || "Branch salary head deleted successfully",
          );
          fetchBranchSalaryHeads(activeBranchId);
        } else {
          showToastNotification(
            "danger",
            response?.message || "Failed to delete branch salary head",
          );
        }
      } catch (error) {
        ThrowError(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [activeBranchId, fetchBranchSalaryHeads],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchSalaryHeads();
  }, [fetchSalaryHeads]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchBranchSalaryHeads(activeBranchId);
  }, [activeBranchId, fetchBranchSalaryHeads]);

  return {
    activeBranchId,
    branches,
    branchSalaryHeads,
    isLoadingBranches,
    isLoadingBranchSalaryHeads,
    isLoadingSalaryHeads,
    isSubmitting,
    selectedBranchId,
    setSelectedBranchId,
    salaryHeads,
    submitBranchSalaryHead,
    deleteBranchSalaryHead,
  };
};
